import type { TSuggestionData } from '@alpod-ai-writer/plate-plugins/suggestion';
import { cn } from '@udecode/cn';
import { type RenderNodeWrapper, usePluginOption } from '@udecode/plate/react';
import { cva } from 'class-variance-authority';
import { CornerDownLeftIcon } from 'lucide-react';
import React from 'react';

import { type SuggestionConfig,suggestionPlugin } from '../../plugins/suggestionPlugin';

const suggestionVariants = cva(
    cn(
        'bg-blue-500/[.08] text-blue-800 no-underline transition-colors duration-200'
    ),
    {
        defaultVariants: {
            insertActive: false,
            remove: false,
            removeActive: false,
        },
        variants: {
            insertActive: {
                false: '',
                true: 'bg-blue-100 text-blue-800',
            },
            remove: {
                false: '',
                true: 'bg-red-100 text-red-700',
            },
            removeActive: {
                false: '',
                true: 'bg-red-200/80 no-underline',
            },
        },
    }
);

export const SuggestionBelowNodes: RenderNodeWrapper<SuggestionConfig> = ({
    api,
    element,
}) => {
    if (!api.suggestion.isBlockSuggestion(element)) return;

    const suggestionData = api.suggestion.suggestionData(element) as TSuggestionData | undefined;
    if (!suggestionData) return;

    return function Component({ children }) {
        return (
            <SuggestionLineBreakContent suggestionData={suggestionData}>
                {children}
            </SuggestionLineBreakContent>
        );
    };
};

function SuggestionLineBreakContent({
    children,
    suggestionData,
}: {
  children: React.ReactNode;
  suggestionData: TSuggestionData;
}) {
    const { isLineBreak, type } = suggestionData;
    const isRemove = type === 'remove';
    const isInsert = type === 'insert';

    const activeSuggestionId = usePluginOption(suggestionPlugin, 'activeId');

    const isActive = activeSuggestionId === suggestionData.id;

    const spanRef = React.useRef<HTMLSpanElement>(null);

    return (
        <>
            {isLineBreak ? (
                <>
                    {children}
                    <span
                        className={cn(
                            'absolute text-justify',
                            suggestionVariants({
                                insertActive: isInsert && isActive,
                                remove: isRemove,
                                removeActive: isActive && isRemove,
                            }),
                            isInsert && 'hover:bg-blue-100 hover:text-blue-800',
                            isRemove && 'hover:bg-red-200/80 hover:no-underline',
                        )}
                        contentEditable={false}
                        ref={spanRef}
                        style={{
                            bottom: 3.5,
                            height: 21,
                        }}
                    >
                        <CornerDownLeftIcon className="mt-0.5 size-4" />
                    </span>
                </>
            ) : (
                <div
                    className={cn(
                        suggestionVariants({
                            insertActive: isInsert && isActive,
                            remove: isRemove,
                            removeActive: isActive && isRemove,
                        }),
                        isInsert && 'hover:bg-blue-100 hover:text-blue-800',
                        isRemove && 'hover:bg-red-200/80 hover:no-underline',
                    )}
                    data-block-suggestion="true"
                >
                    {children}
                </div>
            )}
        </>
    );
}
